import {
  Badge,
  Button,
  Column,
  Columns,
  DropdownButton,
  DropdownButtonContext,
  EmptyState,
  FlexBox,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useMemo } from 'react';

import { notificationsApi } from 'apis/notifications';
import { AppLink } from 'components/appLink';
import { notificationActions } from 'components/notifications/actions/notification';
import { transformAndSortNotifications } from 'components/notifications/components/notificationsPage';
import { useMentionAuthorLookup } from 'components/notifications/hooks/useMentionAuthorLookup';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { TopbarButton } from '../topbarButton';

import { MentionNotificationHistoryItem } from './mentionNotificationHistoryItem';
import { NotificationHistoryItem } from './notificationHistoryItem';
import styles from './notificationTopbarButton.module.scss';

const numberOfVisibleNotifications = 3;

type NotificationTopbarButtonProps = {
  alignCenter?: boolean;
};

export const NotificationTopbarButton = memo(function NotificationTopbarButton({
  alignCenter,
}: NotificationTopbarButtonProps): JSX.Element {
  const dispatch = useAppDispatch();

  const [updateNotificationsAsRead] =
    notificationsApi.useLazyUpdateNotificationsAsReadQuery();

  const { unreadNotifications, isFetching } =
    notificationsApi.useReadNotificationsQuery(
      {
        unreadOnly: false,
      },
      {
        refetchOnMountOrArgChange: false,
        selectFromResult: ({ data, ...rest }) => ({
          unreadNotifications:
            data?.data.notifications.filter(({ is_read }) => !is_read) ?? [],
          ...rest,
        }),
      },
    );

  const authorsById = useMentionAuthorLookup(unreadNotifications);

  const notifications = useMemo(
    () => transformAndSortNotifications(unreadNotifications, authorsById),
    [unreadNotifications, authorsById],
  );

  const onReset = useCallback(async () => {
    const notificationIds = notifications
      .filter(({ payload }) => payload?.type !== 'local_notification:displayed')
      .map(({ id }) => id);

    if (notificationIds.length) {
      // Set is_read for API notifications.
      updateNotificationsAsRead({
        notificationIds,
      });
    }

    // Set is_read for local notifications.
    dispatch(notificationActions.notificationHistoryReset());
  }, [dispatch, notifications, updateNotificationsAsRead]);

  const onToggle = useCallback((isOpen: boolean) => {
    if (isOpen) {
      ampli.topbarNotificationsOpened();
    }
  }, []);

  const visibleNotifications = useMemo(
    () =>
      notifications
        .slice(-numberOfVisibleNotifications) // Last n elements
        .reverse(),
    [notifications],
  );

  const notVisibleNotificationsNumber =
    notifications.length - visibleNotifications.length;

  return (
    <DropdownButton
      icon="bell"
      className={styles.root}
      onToggle={onToggle}
      noArrow
      noStyle
      dropdown={
        <DropdownButtonContext.Consumer>
          {({ closeDropdown }) => (
            <Columns>
              <Column
                className={classnames(
                  {
                    [styles.fullWidthContent]: alignCenter,
                  },
                  styles.content,
                )}
              >
                <Column.Row slottable borderBottom>
                  <Column.Slot placement="start">
                    <Text.Heading>Notifications</Text.Heading>
                  </Column.Slot>

                  <Column.Slot placement="end">
                    {visibleNotifications.length > 0 && (
                      <Button
                        disabled={isFetching}
                        variant="tertiary"
                        onClick={onReset}
                      >
                        Mark all as seen
                      </Button>
                    )}

                    <Badge size="small" variant="info">
                      {notifications.length}
                    </Badge>
                  </Column.Slot>
                </Column.Row>

                <Column.Row
                  scrollable
                  noHorizontalPadding
                  noVerticalPadding={visibleNotifications.length > 0}
                >
                  {visibleNotifications.length ? (
                    visibleNotifications.map((notification) => {
                      if (
                        notification.payload?.type === 'mention:user_mentioned'
                      ) {
                        return (
                          <MentionNotificationHistoryItem
                            key={notification.id}
                            notification={notification}
                            onActionHandled={closeDropdown}
                            userMentionedData={notification.payload.params}
                          />
                        );
                      }
                      return (
                        <NotificationHistoryItem
                          key={notification.id}
                          notification={notification}
                        />
                      );
                    })
                  ) : isFetching ? (
                    <FlexBox
                      height="100%"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Spinner size={32} />
                    </FlexBox>
                  ) : (
                    <EmptyState
                      title="No notifications"
                      description="There are no unread notifications"
                      icon={null}
                    />
                  )}
                </Column.Row>

                <Column.Row slottable noMinHeight borderTop>
                  <Column.Slot placement="center">
                    <AppLink path="/notifications" onClick={closeDropdown}>
                      {notVisibleNotificationsNumber > 0
                        ? `See all notifications (${notVisibleNotificationsNumber} more new)`
                        : 'See all notifications'}
                    </AppLink>
                  </Column.Slot>
                </Column.Row>
              </Column>
            </Columns>
          )}
        </DropdownButtonContext.Consumer>
      }
      dropdownProps={{
        className: classnames({
          [styles.dropdownMenuAlignCenter]: alignCenter,
        }),
      }}
    >
      <TopbarButton
        showNotificationIndicator={notifications.length > 0}
        icon="bell"
        tooltip="Notifications"
      />
    </DropdownButton>
  );
});
